import { apiRequest } from './client'
import type { DocumentTypeIdentification } from '@/types/bundle'

export type TestIdentificationOutcome = 'confirmed' | 'rejected_no_match' | 'no_text_layer'

export interface TestIdentificationRequest {
  documentTypeId: string
  pdfBase64: string
  // legalflow-idp PRD 25 (test-identification, amended for this override -
  // not "PRD 26" as Frontend PRD 03's own text names it; PRD 26 there is
  // unrelated Terraform infra) documents this field as optional, but this
  // screen sends it unconditionally with the current in-memory identification
  // block so Test always reflects unsaved edits (Frontend PRD 03 Inputs).
  identificationOverride: DocumentTypeIdentification
}

interface MatchResultResponse {
  outcome: 'confirmed' | 'rejected_no_match'
  document_type_id: string
  matched_required: string[]
  matched_supporting: string[]
  group_hit: boolean[]
  pages_searched: number[]
  text_source: 'native' | 'ocr'
}

interface NoTextLayerResponse {
  outcome: 'no_text_layer'
  document_type_id: string
}

type TestIdentificationResponse = MatchResultResponse | NoTextLayerResponse

export interface TestIdentificationMatchResult {
  outcome: 'confirmed' | 'rejected_no_match'
  documentTypeId: string
  matchedRequired: string[]
  matchedSupporting: string[]
  groupHit: boolean[]
  pagesSearched: number[]
  textSource: 'native' | 'ocr'
}

export interface TestIdentificationNoTextResult {
  outcome: 'no_text_layer'
  documentTypeId: string
}

export type TestIdentificationResult = TestIdentificationMatchResult | TestIdentificationNoTextResult

// POST /gui/v1/test-identification (legalflow-idp PRD 25) - never touches
// drafts/S3 (PRD 25 AC10/AC11), pure read against the real ID engine.
export async function testIdentification(
  request: TestIdentificationRequest,
): Promise<TestIdentificationResult> {
  const response = await apiRequest<TestIdentificationResponse>('/gui/v1/test-identification', {
    method: 'POST',
    body: JSON.stringify({
      document_type_id: request.documentTypeId,
      pdf_base64: request.pdfBase64,
      identification_override: request.identificationOverride,
    }),
  })

  if (response.outcome === 'no_text_layer') {
    return { outcome: 'no_text_layer', documentTypeId: response.document_type_id }
  }

  return {
    outcome: response.outcome,
    documentTypeId: response.document_type_id,
    matchedRequired: response.matched_required,
    matchedSupporting: response.matched_supporting,
    groupHit: response.group_hit,
    pagesSearched: response.pages_searched,
    textSource: response.text_source,
  }
}
